'use strict'

const cloneDeep_ = require('lodash/cloneDeep')
const isEqual_ = require('lodash/isEqual')
const flatten_ = require('lodash/flatten')
const omit_ = require('lodash/omit')

const { isSameRecord } = require('../record-store/records')

const {
  isCallBeforeSaveHooks,
  isCheckHasDraft,
  isAssertValidity,
  isSaveRecord,
  isSendAutomationEvent
} = require('./saveStates')
const {
  getPaginationData,
  selectCurrentRecord,
  selectCurrentRecordIndex,
  selectDefaultDraft,
  selectDesiredRecordIndex,
  selectFieldsToUpdate,
  selectRefreshCurrentRecord,
  selectRemoveCurrentRecord,
  selectRefreshCurrentView,
  selectRevertChanges,
  selectSaveState,
  selectUpdateSource,
  selectNewRecordIndex
} = require('./reducer')

const {
  assertValidityCmd,
  notifyIndexChangeCmd,
  notifyRecordSetLoadedCmd,
  updateCurrentViewCmd
} = require('../components')
const {
  callBeforeSaveHooksCmd,
  checkHasDraftCmd,
  getRecordByIndexCmd,
  revertChangesCmd,
  removeCurrentRecordCmd,
  saveRecordCmd,
  setFieldsInCurrentRecordCmd,
  newRecordCmd
} = require('../records/executor')
const { fireEventCmd } = require('../helpers/fireEventExecutor')
const {
  sendAutomationEventCmd
} = require('../helpers/sendAutomationsEventExecutor')
const { verboseEvent } = require('../dataset-api/verbosity')

const omitSystemFields = record =>
  omit_(record, ['_id', '_createdDate', '_updatedDate'])

const subscriber = recordStore => {
  const processIndexChanges = ({ from, to, hasChanged, hasChangedToMatch }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const fromCurrentIndex = selectCurrentRecordIndex(from)
    const toDesiredIndex = selectDesiredRecordIndex(to)
    const toRefreshCurrentRecord = selectRefreshCurrentRecord(to)
    const fromRefreshCurrentRecord = selectRefreshCurrentRecord(from)
    const commandsToReturn = []

    if (
      hasChangedToMatch(
        selectDesiredRecordIndex,
        desiredRecordIndex =>
          desiredRecordIndex >= 0 && desiredRecordIndex !== toCurrentIndex
      ) ||
      (toRefreshCurrentRecord && !fromRefreshCurrentRecord)
    ) {
      recordStore().matchWith({
        Error: ({ value: error }) => {
          throw new Error(error)
        },
        Ok: ({ value: service }) => {
          const totalCount = service.getMatchingRecordCount()
          const realIndex = Math.max(
            Math.min(toDesiredIndex, totalCount - 1),
            0
          )

          if (fromCurrentIndex !== realIndex || toRefreshCurrentRecord) {
            commandsToReturn.push(getRecordByIndexCmd(realIndex))
          }
        }
      })
    }

    if (hasChanged(selectCurrentRecordIndex) && fromCurrentIndex >= 0) {
      commandsToReturn.push(notifyIndexChangeCmd())
      commandsToReturn.push(
        fireEventCmd('currentIndexChanged', selectCurrentRecordIndex(to))
      )
    }

    return commandsToReturn
  }

  const processRecordChanges = ({
    from,
    to,
    hasChangedToMatch,
    hasChangedToTrue
  }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const toCurrentRecord = selectCurrentRecord(to)
    const fromCurrentRecord = selectCurrentRecord(from)
    const commandsToReturn = []

    if (
      isSameRecord(fromCurrentRecord, toCurrentRecord) &&
      !isEqual_(
        omitSystemFields(fromCurrentRecord),
        omitSystemFields(toCurrentRecord)
      )
    ) {
      commandsToReturn.push(
        fireEventCmd(
          'itemValuesChanged',
          cloneDeep_(fromCurrentRecord),
          cloneDeep_(toCurrentRecord)
        )
      )
    }

    // During the save process we can get here if:
    // - Saving a new record: wixData will add system fields
    // - Saving an existing record with a backend hook that updates the record
    if (
      !isEqual_(fromCurrentRecord, toCurrentRecord) &&
      isSaveRecord(selectSaveState(from))
    ) {
      const beforeSaveRecord = cloneDeep_(fromCurrentRecord)
      const afterSaveRecord = cloneDeep_(toCurrentRecord)

      verboseEvent('afterSave', { beforeSaveRecord, afterSaveRecord })
      commandsToReturn.push(
        fireEventCmd('afterSave', beforeSaveRecord, afterSaveRecord)
      )
    }

    const toFieldsToUpdate = selectFieldsToUpdate(to)
    if (
      hasChangedToMatch(
        selectFieldsToUpdate,
        fieldsToUpdate => fieldsToUpdate != null
      )
    ) {
      commandsToReturn.push(
        setFieldsInCurrentRecordCmd(
          toFieldsToUpdate,
          toCurrentIndex,
          selectUpdateSource(to)
        )
      )
    }

    if (hasChangedToTrue(selectRevertChanges)) {
      commandsToReturn.push(
        revertChangesCmd(toCurrentIndex, selectDefaultDraft(to))
      )
    }

    if (hasChangedToTrue(selectRemoveCurrentRecord)) {
      commandsToReturn.push(removeCurrentRecordCmd(toCurrentIndex))
    }

    return commandsToReturn
  }

  const processViewChanges = ({ hasChanged, hasChangedToTrue }) => {
    const commandsToReturn = []

    if (hasChanged(getPaginationData)) {
      commandsToReturn.push(notifyRecordSetLoadedCmd())
    }

    if (hasChangedToTrue(selectRefreshCurrentView)) {
      commandsToReturn.push(updateCurrentViewCmd())
    }

    return commandsToReturn
  }

  const processSaveStates = ({ from, to, hasChangedToMatch }) => {
    const toCurrentIndex = selectCurrentRecordIndex(to)
    const toCurrentRecord = selectCurrentRecord(to)

    if (hasChangedToMatch(selectSaveState, isCallBeforeSaveHooks)) {
      return callBeforeSaveHooksCmd()
    } else if (hasChangedToMatch(selectSaveState, isCheckHasDraft)) {
      return checkHasDraftCmd(toCurrentIndex)
    } else if (hasChangedToMatch(selectSaveState, isAssertValidity)) {
      return assertValidityCmd(toCurrentRecord)
    } else if (hasChangedToMatch(selectSaveState, isSaveRecord)) {
      return saveRecordCmd(toCurrentIndex)
    } else if (hasChangedToMatch(selectSaveState, isSendAutomationEvent)) {
      return sendAutomationEventCmd(toCurrentRecord)
    }
  }

  const processNewRecord = ({ from, to, hasChangedToNotNull }) => {
    const commandsToReturn = []
    if (hasChangedToNotNull(selectNewRecordIndex)) {
      commandsToReturn.push(
        newRecordCmd(selectNewRecordIndex(to), selectDefaultDraft(to))
      )
    }

    return commandsToReturn
  }

  return transition =>
    flatten_(
      [
        processIndexChanges,
        processRecordChanges,
        processViewChanges,
        processSaveStates,
        processNewRecord
      ].map(fn => fn(transition))
    )
}

module.exports = subscriber



// WEBPACK FOOTER //
// ./records/subscriber.js